import { Head, Link } from '@inertiajs/react'
import { ArrowRight, Inbox, MessageSquare } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type TeamChatroomSummary = {
  id: string
  teamName: string
  teamPrefecture?: string | null
  lastMessage?: string | null
  lastMessageAt?: string | null
  lastMessageSender?: string | null
}

export type SchoolTeamChatroomsIndexProps = {
  teamChatrooms: TeamChatroomSummary[]
  breadcrumbs: Breadcrumb[]
}

export default function SchoolTeamChatroomsIndex({ teamChatrooms, breadcrumbs }: SchoolTeamChatroomsIndexProps) {
  return (
    <AppShell>
      <Head title="チームコーチへのチャット" />
      <PageHeader
        title="チームコーチへのチャット"
        description="選手の所属チームのコーチとのやり取りをまとめて確認できます。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="secondary" size="sm">
            <Link href="/school/players">
              選手を探す
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        }
      />

      <Card padding="lg" className="space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">進行中のチャット</h2>
            <p className="text-xs text-white/50">最新の更新順で表示しています</p>
          </div>
          <span className="rounded-full bg-white/10 px-4 py-1 text-xs text-white/60">
            {teamChatrooms.length} 件
          </span>
        </header>

        {teamChatrooms.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="チャット履歴がありません"
            description="気になる選手の詳細ページから「チームコーチに連絡」を選ぶと、ここに履歴が表示されます。"
            action={
              <Button asChild size="sm">
                <Link href="/school/players">
                  選手を探す
                  <MessageSquare className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {teamChatrooms.map((teamChatroom) => (
              <Link
                key={teamChatroom.id}
                href={`/school/team_chatrooms/${teamChatroom.id}`}
                className="flex flex-col gap-2 rounded-3xl border border-white/10 bg-white/5 p-4 transition hover:border-white/20 hover:bg-white/10"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">{teamChatroom.teamName}</p>
                    <p className="text-xs text-white/60">{teamChatroom.teamPrefecture ?? '所在地未登録'}</p>
                  </div>
                  {teamChatroom.lastMessageAt ? (
                    <time className="text-xs text-white/40">{formatDateTime(teamChatroom.lastMessageAt)}</time>
                  ) : null}
                </div>
                <p className="line-clamp-2 text-xs text-white/70">
                  {teamChatroom.lastMessage ? teamChatroom.lastMessage : 'メッセージはまだありません'}
                </p>
                {teamChatroom.lastMessageSender ? (
                  <p className="text-[11px] text-white/40">最後の送信者: {teamChatroom.lastMessageSender}</p>
                ) : null}
              </Link>
            ))}
          </div>
        )}
      </Card>
    </AppShell>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
